import type { ProcessedFieldMetadata, EnrichedTableField } from '../types/metadata';
import type { FormField } from '../types/ui';

// Generic path-based value getter with proper typing
export function getNestedValue<T = any>(
  obj: Record<string, any>, 
  path: string
): T | undefined {
  const effectivePath = path.split('.');
  return effectivePath.reduce<any>((acc, key) => (acc && acc[key] !== undefined) ? acc[key] : undefined, obj);
}

// Type-safe nested value setter
export function setNestedValue<T extends Record<string, any>>(
  obj: T, 
  path: string, 
  value: any
): T {
  const effectivePath = path.split('.');
  const newObj = { ...obj };

  effectivePath.reduce<any>((acc, key, index) => {
    if (index === effectivePath.length - 1) {
      acc[key] = value;
    } else {
      if (acc[key] === undefined) {
        acc[key] = {};
      } else {
        acc[key] = { ...acc[key] };
      }
      return acc[key];
    }
  }, newObj);
  return newObj as T;
}

// Critical metadata lookup function with complex generic typing
export function getDBColFromDataIndex(
  dataIndex: string, 
  fieldData: ProcessedFieldMetadata, 
  tableName: string
): EnrichedTableField | null {
  if (!dataIndex || typeof dataIndex !== 'string') {
    console.warn('[getDBColFromDataIndex] Invalid dataIndex:', dataIndex);
    return null;
  }
  const effectivePath = dataIndex.split('.');
  
  // Handle relationship fields
  if (effectivePath && effectivePath[0].endsWith('_rel')) {
    const localFieldName = effectivePath[0].replace('_rel', '');
    const targetFieldName = effectivePath[1];
    const relDbCol = fieldData[tableName]?.byFieldName[localFieldName];
    if (!relDbCol?.relationship) {
      return null;
    }
    const targetTable = relDbCol.relationship.target_table_name;
    return fieldData[targetTable]?.byFieldName[targetFieldName] || null;
  }

  if (effectivePath && effectivePath[0].endsWith('_calc')) {
    return fieldData[tableName]?.byFieldName[effectivePath[0].replace('_calc', '')] || null;
  }

  return fieldData[tableName]?.byFieldName[effectivePath[0]] || null;
}

// Field readonly checker with proper field typing
export function fieldIsReadOnly(field: FormField): boolean {
  return field.editable === false || field.dbCol?.is_system === true || field.dbCol?.is_calculated === true;
}

// Local storage utilities with generic typing
const localStoragePrefix = 'supa-crm';

export function setLocalStore<T>(key: string, value: T): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(`${localStoragePrefix}-${key}`, JSON.stringify(value));
  }
}

export function getLocalStore<T>(key: string): T | null {
  if (typeof window !== 'undefined') {
    const item = localStorage.getItem(`${localStoragePrefix}-${key}`);
    if (item === null) {
      return null;
    }
    try {
      return JSON.parse(item) as T;
    } catch {
      return null;
    }
  }
  return null;
}

export function clearLocalStore(key: string): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(`${localStoragePrefix}-${key}`);
  }
}

// Smart value comparison that handles arrays, numbers, and null/undefined
export function valuesAreEqual(a: any, b: any): boolean {
  // Handle null/undefined equivalence
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;

  // Handle array comparison (for multi-picklists and other array fields)
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
  }

  // Handle number comparison (catch string/number type mismatches)
  if (typeof a === 'number' || typeof b === 'number') {
    // Only coerce if at least one is actually a number
    const aNum = Number(a);
    const bNum = Number(b);
    // Check if conversion was valid (not NaN)
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum === bNum;
    }
  }

  // Default: strict equality
  return a === b;
}